/* eslint-disable react/display-name */
import React, { useState, forwardRef } from 'react';

import clsx from 'clsx';
import { Box, Badge, Button, colors, Collapse, ListItem } from '@mui/material';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { NavLink as RouterLink } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  WAITING_ROOM_PATH,
  WAITING_ROOM_SUPERVISOR_PATH,
} from '@constants/SESSION';
import { selectWaitingCount } from '@state/selectors';
import { useSelector } from 'react-redux';

const CustomRouterLink = forwardRef((props, ref) => (
  <div ref={ref} style={{ flexGrow: 1 }}>
    <RouterLink {...props} />
  </div>
));

const useStyles = makeStyles((theme) => ({
  item: {
    display: 'block',
    paddingTop: 0,
    paddingBottom: 0,
  },
  itemLeaf: {
    display: 'flex',
    paddingTop: 0,
    paddingBottom: 0,
  },
  button: {
    color: colors.blueGrey[800],
    padding: '10px 8px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
  },
  buttonLeaf: {
    color: colors.blueGrey[800],
    padding: '10px 8px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    fontWeight: theme.typography.fontWeightRegular,
    '&.depth-0': {
      fontWeight: theme.typography.fontWeightMedium,
    },
  },
  icon: {
    display: 'flex',
    fontSize: '21px',
    alignItems: 'center',
    color: theme.palette.icon,
    marginRight: theme.spacing(1),
  },
  expandIcon: {
    marginLeft: 'auto',
    height: 16,
    width: 16,
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
  },
  active: {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightMedium,
    '& $icon': {
      color: theme.palette.primary.main,
    },
  },
}));

const NavigationListItem = ({
  href,
  title,
  depth,
  children,
  className,
  isFullNav,
  icon: Icon,
  label: Label,
  open: openProp,
  ...rest
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(openProp);

  const waitingCount = useSelector(selectWaitingCount);

  const isWaitingRoomOptiop = [
    WAITING_ROOM_PATH,
    WAITING_ROOM_SUPERVISOR_PATH,
  ].includes(href);

  const handleToggle = () => {
    setOpen((open) => !open);
  };

  let paddingLeft = 8;
  let opacity = 1;

  if (depth > 0) {
    opacity = 0.65;
  }

  const style = {
    paddingLeft,
    opacity,
  };

  if (children) {
    return (
      <ListItem
        {...rest}
        className={clsx(classes.item, className)}
        disableGutters>
        <Button className={classes.button} onClick={handleToggle} style={style}>
          {Icon && <Icon className={classes.icon} />}
          {isFullNav && title}
          {open ? (
            <ExpandLessIcon className={classes.expandIcon} color="inherit" />
          ) : (
            <ExpandMoreIcon className={classes.expandIcon} color="inherit" />
          )}
        </Button>
        <Collapse in={open}>{children}</Collapse>
      </ListItem>
    );
  } else {
    return (
      <ListItem
        {...rest}
        className={clsx(classes.itemLeaf, className)}
        disableGutters>
        <Button
          activeClassName={classes.active}
          className={clsx(classes.buttonLeaf, `depth-${depth}`)}
          component={CustomRouterLink}
          exact
          style={style}
          to={href}>
          {Icon && <Icon className={classes.icon} />}
          {isFullNav && title}
          {Label && (
            <span className={classes.label}>
              <Label />
            </span>
          )}
          {isWaitingRoomOptiop && (
            <Box position="absolute" right={16}>
              <Badge color="secondary" badgeContent={waitingCount} />
            </Box>
          )}
        </Button>
      </ListItem>
    );
  }
};

NavigationListItem.defaultProps = {
  depth: 0,
  open: false,
  isFullNav: true,
};

NavigationListItem.propTypes = {
  icon: PropTypes.any,
  label: PropTypes.any,
  open: PropTypes.bool,
  href: PropTypes.string,
  children: PropTypes.node,
  isFullNav: PropTypes.bool,
  className: PropTypes.string,
  title: PropTypes.string.isRequired,
  depth: PropTypes.number.isRequired,
};

export default NavigationListItem;
